import { useState } from 'react';
import { Dialog, DialogContent } from './Dialog.js';
import { Button } from './Button.js';
import { Field, Input, Textarea } from './Input.js';

/**
 * Every admin action requires a reason (AC-ADMGL-04) — this dialog is the
 * only way one is submitted. Irreversible actions additionally require typing
 * the target's name (AC-ADMGL-08). Reason min length matches the contract (10).
 */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  danger,
  typeToConfirm,
  onConfirm,
  loading,
  extra,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  danger?: boolean;
  /** When set, the user must type this exact string. */
  typeToConfirm?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  loading?: boolean;
  extra?: React.ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const ok = reason.trim().length >= 10 && (!typeToConfirm || typed === typeToConfirm);
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          setReason('');
          setTyped('');
        }
      }}
    >
      <DialogContent title={title} description={description}>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (ok) await onConfirm(reason.trim());
          }}
        >
          {extra}
          <Field label="Reason" required hint="Recorded in the audit log. Minimum 10 characters.">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              placeholder="Why are you taking this action?"
            />
          </Field>
          {typeToConfirm && (
            <Field label={`Type “${typeToConfirm}” to confirm`} required>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={typeToConfirm}
                autoComplete="off"
              />
            </Field>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant={danger ? 'danger' : 'primary'}
              disabled={!ok}
              loading={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
